/**
 * Every connector of the scene, from placed cards. Line weight is meaning: a
 * causal claim is a thick orthogonal connector in front, an association a thin
 * curve behind. Connectors that go to the same target join into one trunk as
 * early as their sources allow, so the map has fewer lines than claims; every
 * edge still returns its own full route (stub + trunk), label and knot.
 */
import { labelAndKnot, curveBetween, orthD, isCausalType } from './routeEdges';
import { polylineD } from './geometry';

export function buildEdges({ edges, cardById, tree, primaryInto, blockersOnEdge, laneOfId, router, cfg, missionId, laneIds }) {
  const out = [];
  const bundles = [];
  const unrouted = [];
  const orthEdge = (base, s, t, opts) => {
    const r = router.orthogonal(s, t, opts);
    const { label, knot } = labelAndKnot(r.points, cfg);
    return { ...base, points: r.points, path: orthD(r.points, cfg), label, knot, via: r.via, clear: r.clear !== false, forward: t.column > s.column };
  };

  if (missionId && cardById.has(missionId)) {
    laneIds.forEach((id) => {
      if (!cardById.has(id)) return;
      const e = orthEdge({ id: `mission:${id}`, from: missionId, to: id, type: null, kind: 'structural', causal: true, primary: false, tree: true, cross: false, behind: false, blockers: [], stubs: [] }, cardById.get(missionId), cardById.get(id), { tree: true });
      out.push({ ...e, label: null });
    });
  }

  // What each edge is, before any routing.
  const items = [];
  edges.forEach((e) => {
    const { from, to, type } = e.attrs;
    const s = cardById.get(from);
    const t = cardById.get(to);
    if (!s || !t) { unrouted.push(e.id); return; }
    const causal = isCausalType(type);
    const isTree = tree.parentOf.get(to) === from && primaryInto.get(to) === e;
    const on = isTree && causal ? (blockersOnEdge.get(e.id) || []) : [];
    const base = {
      id: e.id, from, to, type: type || null, causal, primary: Boolean(e.attrs.primary), tree: isTree, cross: laneOfId(from) !== laneOfId(to), blockers: on, stubs: [],
      state: e.state || 'accepted', reason: e.claims?.[0]?.reason || null, // a proposal's one-line why, for the room's decision
    };
    items.push({ e, s, t, causal, isTree, on, base });
  });

  // A link joins the tree's line out of its card when a tree connector of its type
  // leaves there too: the connectors of one type run together (sceneLabels.js).
  const treeTypes = new Map();
  items.forEach((it) => {
    if (!it.isTree || !it.causal) return;
    if (!treeTypes.has(it.s.id)) treeTypes.set(it.s.id, new Set());
    treeTypes.get(it.s.id).add(it.base.type);
  });
  const onTreeLine = (it) => it.isTree || Boolean(treeTypes.get(it.s.id)?.has(it.base.type));

  // Bundles: causal, unblocked, forward connectors sharing a target — or, for
  // goals, sharing the lane's goal stack: one trunk into the goal column that
  // branches at the end. A stack is one column: the shared targets and the end
  // state after them are two stacks, or the trunk's spine would stand right of
  // the targets and every branch would run back through its card.
  const groups = new Map();
  if (cfg.bundle !== false) {
    items.forEach((it) => {
      if (!it.causal || it.on.length || it.t.column <= it.s.column) return;
      const key = it.t.pinned ? `goals:${it.t.lane}:${it.t.column}` : it.t.id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    });
  }
  const inBundle = new Map();
  groups.forEach((members, key) => {
    if (members.length < 2) return;
    const targets = [...new Set(members.map((m) => m.t))];
    // A proposal's stub is dashed and joins the trunk; the trunk itself is dashed only
    // when nothing accepted carries it.
    const proposed = (m) => m.e.state === 'proposed';
    const b = router.bundle(targets, members.map((m) => ({ s: m.s, t: m.t, tree: m.isTree, treeLine: onTreeLine(m), proposed: proposed(m), type: m.base.type })));
    const id = `bundle:${key}`;
    // One word per branch into a target, on the run every member to it shares,
    // when they all say the same thing; otherwise each stub carries its own.
    const labels = targets.map((t) => {
      const mine = members.map((m, i) => ({ m, r: b.routes[i] })).filter((x) => x.m.t === t);
      const types = [...new Set(mine.map((x) => x.m.base.type))];
      const end = { x: t.x, y: t.cy };
      const from = b.via === 'trunk-row' ? Math.max(...mine.map((x) => x.r.stub[x.r.stub.length - 1].x)) : b.spineX;
      const run = labelAndKnot([{ x: from, y: t.cy }, end], cfg);
      return { to: t.id, type: types.length === 1 ? types[0] : null, label: types.length === 1 ? run.label : null, knot: run.knot };
    });
    const labelOf = new Map(labels.map((l) => [l.to, l]));
    bundles.push({ id, to: targets.map((t) => t.id), members: members.map((m) => m.e.id), proposed: members.every(proposed), via: b.via, lines: b.lines.map((points) => ({ points, path: orthD(points, cfg) })), labels });
    members.forEach((m, i) => {
      const r = b.routes[i];
      const branch = labelOf.get(m.t.id);
      inBundle.set(m.e.id, {
        ...m.base,
        kind: m.isTree ? 'primary' : 'link',
        behind: false,
        bundle: id,
        stub: { points: r.stub, path: orthD(r.stub, cfg) },
        points: r.points,
        path: orthD(r.points, cfg),
        label: branch.type ? branch.label : labelAndKnot(r.stub, cfg).label,
        labelOn: branch.type ? 'trunk' : 'stub',
        knot: branch.knot,
        via: b.via,
        clear: r.clear,
        forward: true,
      });
    });
  });

  items.forEach(({ e, s, t, causal, isTree, on, base }) => {
    if (inBundle.has(e.id)) { out.push(inBundle.get(e.id)); return; }
    if (!causal) {
      out.push({ ...base, kind: 'association', behind: true, ...curveBetween(s, t, cfg), forward: t.column > s.column, via: 'curve', clear: true });
      return;
    }
    const first = on.length ? cardById.get(on[0]) : t;
    const edge = orthEdge({ ...base, kind: isTree ? 'primary' : 'link', behind: false }, s, first, { tree: isTree, treeLine: onTreeLine({ isTree, s, base }), preferBeforeTarget: t.pinned === true, type: base.type });
    // The cut stub: from each blocker to the next, and from the last into the target.
    const chain = [...on.map((id) => cardById.get(id)), t];
    for (let i = 1; i < chain.length; i += 1) {
      const pts = [{ x: chain[i - 1].x + chain[i - 1].w, y: t.cy }, { x: chain[i].x, y: t.cy }];
      edge.stubs.push({ points: pts, path: polylineD(pts) });
    }
    edge.forward = t.column > s.column;
    out.push(edge);
  });

  // What the renderer draws in front, as polylines: each trunk once, each
  // bundled edge's stub, every other causal connector whole.
  const drawn = [];
  bundles.forEach((b) => b.lines.forEach((l) => drawn.push(l.points)));
  out.forEach((e) => {
    if (e.behind) return;
    drawn.push(e.bundle ? e.stub.points : e.points);
    e.stubs.forEach((s) => drawn.push(s.points));
  });

  return { edges: out, bundles, unrouted, drawn };
}
